import axios from '@/plugins/axios';

const blobUrlByKey = new Map();
const inflightByKey = new Map();
const generationByMediaId = new Map();

function makeKey(mediaId, imageType) {
    return `${mediaId}:${imageType}`;
}

function getGeneration(mediaId) {
    return generationByMediaId.get(mediaId) || 0;
}

async function fetchAndStore(mediaId, imageType) {
    const key = makeKey(mediaId, imageType);
    if (blobUrlByKey.has(key)) return blobUrlByKey.get(key);
    if (inflightByKey.has(key)) return inflightByKey.get(key);

    const generationAtStart = getGeneration(mediaId);

    const promise = axios
        .get(`/media/${mediaId}/image`, {
            params: { image_type: imageType },
            responseType: 'blob',
        })
        .then((response) => {
            const url = URL.createObjectURL(response.data);
            // If invalidate(mediaId) ran while this was in flight, the generation
            // changed — discard this URL instead of poisoning the cache with stale data.
            if (getGeneration(mediaId) !== generationAtStart) {
                URL.revokeObjectURL(url);
                inflightByKey.delete(key);
                return null;
            }
            blobUrlByKey.set(key, url);
            inflightByKey.delete(key);
            return url;
        })
        .catch((error) => {
            inflightByKey.delete(key);
            throw error;
        });

    inflightByKey.set(key, promise);
    return promise;
}

function resolveImageType(viewType, attentionMapStatus) {
    if ((viewType === 'heatmap' || viewType === 'fogmap') && attentionMapStatus === 'completed') {
        return viewType;
    }
    return 'original';
}

export function useMediaImageCache() {
    function getOrFetch(mediaId, imageType) {
        if (!mediaId || !imageType) return Promise.resolve(null);
        return fetchAndStore(mediaId, imageType);
    }

    function prefetchVariants(mediaId, attentionMapStatus) {
        if (!mediaId) return Promise.resolve([]);
        const types = attentionMapStatus === 'completed'
            ? ['original', 'heatmap', 'fogmap']
            : ['original'];
        return Promise.all(types.map((t) => fetchAndStore(mediaId, t).catch(() => null)));
    }

    function invalidate(mediaId) {
        if (!mediaId) return;
        // Bump generation so any in-flight fetches for this mediaId are discarded on settle.
        // We do NOT cancel the underlying axios requests — the in-flight promise still
        // resolves, but the new generation check in fetchAndStore prevents poisoning the cache.
        generationByMediaId.set(mediaId, getGeneration(mediaId) + 1);
        for (const key of Array.from(blobUrlByKey.keys())) {
            if (key.startsWith(`${mediaId}:`)) {
                URL.revokeObjectURL(blobUrlByKey.get(key));
                blobUrlByKey.delete(key);
            }
        }
        for (const key of Array.from(inflightByKey.keys())) {
            if (key.startsWith(`${mediaId}:`)) inflightByKey.delete(key);
        }
    }

    return { getOrFetch, prefetchVariants, invalidate, resolveImageType };
}

export function clearMediaImageCache() {
    for (const url of blobUrlByKey.values()) URL.revokeObjectURL(url);
    blobUrlByKey.clear();
    inflightByKey.clear();
    generationByMediaId.clear();
}

export function __resetCacheForTests() {
    for (const url of blobUrlByKey.values()) URL.revokeObjectURL(url);
    blobUrlByKey.clear();
    inflightByKey.clear();
    generationByMediaId.clear();
}
